"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.accessCodeValidation = exports.postalCodeValidation = exports.getPostalCodeRegex = exports.phoneValidation = exports.emailValidation = exports.ageValidation = exports.twoWheelNumberValidation = exports.bicycleNumberValidation = exports.carNumberValidation = exports.householdSizeValidation = exports.inputRangeValidation = exports.optionalValidation = exports.requiredValidation = void 0;
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const LodashExtensions_1 = require("chaire-lib-common/lib/utils/LodashExtensions");
const project_config_1 = __importDefault(require("../../../config/project.config"));
/**
 * Make sure the question is answered.
 *
 * @see {@link ValidationFunction}
 */
const requiredValidation = (value) => {
    return [
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: {
                fr: 'Cette réponse est requise.',
                en: 'This answer is required.'
            }
        }
    ];
};
exports.requiredValidation = requiredValidation;
/**
 * Optional question.
 *
 * @see {@link ValidationFunction}
 */
const optionalValidation = () => [];
exports.optionalValidation = optionalValidation;
/**
 * Make sure the InputRange is answered with a positive number.
 *
 * The value must be a positive number or 'na'.
 *
 * @see {@link ValidationFunction}
 */
const inputRangeValidation = (value) => {
    return [
        {
            // Check if the value is less than 0 and not 'na'
            validation: !(Number(value) >= 0) && value !== 'na',
            errorMessage: {
                fr: 'Cette réponse doit être d\'une valeur minimum de 0.',
                en: 'This answer must be a minimum value of 0.'
            }
        },
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: {
                fr: 'Cette réponse est requise.',
                en: 'This answer is required.'
            }
        }
    ];
};
exports.inputRangeValidation = inputRangeValidation;
/**
 * Verify if the value is a valid household size.
 *
 * The household size must be an integer between 1 and 18.
 *
 * @see {@link ValidationFunction}
 */
const householdSizeValidation = (value) => {
    return [
        {
            validation: isNaN(Number(value)) || !Number.isInteger(Number(value)),
            errorMessage: (t) => t('survey:errors:householdSizeInvalid')
        },
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: (t) => t('survey:errors:householdSizeRequired')
        },
        {
            validation: Number(value) > project_config_1.default.maxHouseholdSize,
            errorMessage: (t) => t('survey:errors:householdSizeOverMax', { max: project_config_1.default.maxHouseholdSize })
        },
        {
            validation: Number(value) <= 0,
            errorMessage: (t) => t('survey:errors:householdSizeMinOne')
        }
    ];
};
exports.householdSizeValidation = householdSizeValidation;
var householdAssetCountValidation_1 = require("./householdAssetCountValidation");
Object.defineProperty(exports, "carNumberValidation", { enumerable: true, get: function () { return householdAssetCountValidation_1.carNumberValidation; } });
Object.defineProperty(exports, "bicycleNumberValidation", { enumerable: true, get: function () { return householdAssetCountValidation_1.bicycleNumberValidation; } });
Object.defineProperty(exports, "twoWheelNumberValidation", { enumerable: true, get: function () { return householdAssetCountValidation_1.twoWheelNumberValidation; } });
/**
 * Verify if the value is a valid age.
 *
 * The age must be an integer between 0 and {@link projectConfig.maxPersonAge}.
 *
 * @see {@link ValidationFunction}
 */
const ageValidation = (value) => {
    return [
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: {
                fr: 'L\'âge est requis.',
                en: 'Age is required.'
            }
        },
        {
            validation: isNaN(Number(value)) || !Number.isInteger(Number(value)),
            errorMessage: {
                fr: 'L\'âge est invalide.',
                en: 'Age is invalid.'
            }
        },
        {
            validation: Number(value) < 0,
            errorMessage: {
                fr: 'L\'âge doit être au moins de 0.',
                en: 'Age must be at least 0.'
            }
        },
        {
            validation: Number(value) > project_config_1.default.maxPersonAge,
            errorMessage: {
                fr: 'L\'âge est trop élevé, veuillez vérifier.',
                en: 'Age is too high, please validate.'
            }
        }
    ];
};
exports.ageValidation = ageValidation;
/**
 * Verify if the value is a valid email.
 *
 * The email must be in a valid email format.
 *
 * @see {@link ValidationFunction}
 */
const emailValidation = (value) => {
    return [
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: {
                fr: 'Le courriel est requis.',
                en: 'Email is required.'
            }
        },
        {
            validation: !(0, LodashExtensions_1._isBlank)(value) &&
                !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(String(value)),
            errorMessage: {
                fr: 'Le courriel est invalide.',
                en: 'Email is invalid'
            }
        }
    ];
};
exports.emailValidation = emailValidation;
// Regexes developed from discussion with AI
const northAmericanPhoneValidationRegex = /^(?:(?:\+1[-.\s]?|(?!1)?)?(?:\(?([2-9][0-9]{2})\)?[-.\s]?)?([0-9]{3})[-.\s]?([0-9]{4}))$/;
const internationalNotNaPhoneValidationRegex = /^(?:\+(?:[\s\-.()]*\d){10,15}[\s\-.()]*$)/;
const validateNorthAmericanOrInternationalPhoneNumber = (phoneNumber) => northAmericanPhoneValidationRegex.test(phoneNumber) || internationalNotNaPhoneValidationRegex.test(phoneNumber);
/**
 * Verify if the value is a valid phone number. This validation is optional.
 * Note if only validates the format, not the content.
 *
 * The phone number must be in the format 123-456-7890.
 *
 * TODO This validation is from a north american perspective, ie numbers in
 * north america do not need the country code, other international numbers do.
 * We should support more localizations in the future.
 *
 * @see {@link ValidationFunction}
 */
const phoneValidation = (value) => {
    return [
        {
            // Trim and remove all whitespace characters (spaces, tabs, non-breaking spaces, etc.) before validating
            validation: !(0, LodashExtensions_1._isBlank)(value) && !validateNorthAmericanOrInternationalPhoneNumber(String(value).replace(/\s+/g, '')),
            errorMessage: (t) => t('survey:errors:phoneNumberInvalid')
        }
    ];
};
exports.phoneValidation = phoneValidation;
// To be valid in Canada, the postal code cannot have the letters D, F, I, O, Q, or U. It also cannot have W or Z in the first character.
// See: https://en.wikipedia.org/wiki/Postal_codes_in_Canada#Number_of_possible_postal_codes
const canadianPostalCodeRegex = /^[abceghj-nprstvxy][0-9][abceghj-nprstv-z]( )?[0-9][abceghj-nprstv-z][0-9]\s*$/i;
// Quebec postal codes starts with 'G', 'H' or 'J'. Some 'K' also exist in Gatineau. See https://www150.statcan.gc.ca/n1/pub/92-195-x/2011001/other-autre/pc-cp/tbl/tbl9-eng.htm
const quebecPostalCodeRegex = /^[ghjk][0-9][abceghj-nprstv-z]( )?[0-9][abceghj-nprstv-z][0-9]\s*$/i;
// Other region postal codes can be any string
const otherPostalCodeRegex = /^.*$/;
/**
 * Get the appropriate postal code regex based on the configured region.
 *
 * TODO Support more countries and regions
 *
 * @returns The regular expression for validating postal codes in the configured
 * region
 */
const getPostalCodeRegex = () => {
    switch (project_config_1.default.postalCodeRegion) {
        case 'canada':
            return canadianPostalCodeRegex;
        case 'quebec':
            return quebecPostalCodeRegex;
        case 'other':
        default:
            return otherPostalCodeRegex;
    }
};
exports.getPostalCodeRegex = getPostalCodeRegex;
/**
 * Verify the value is a valid postal code.
 *
 * The postal code must be in a valid Canadian format.
 *
 * @see {@link ValidationFunction}
 */
const postalCodeValidation = (value) => {
    return [
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: (t) => t('survey:errors:postalCodeRequired')
        },
        {
            validation: !(0, exports.getPostalCodeRegex)().test(String(value)),
            errorMessage: (t) => t('survey:errors:postalCodeInvalid', {
                context: project_config_1.default.postalCodeRegion
            })
        }
    ];
};
exports.postalCodeValidation = postalCodeValidation;
// Eight digits access code validation regex
const eightDigitsAccessCodeValidation = /^\d{4}-? *\d{4}$/i;
// FIXME Support more access code formats in the future. For now, only one format is supported if using this validation function
const getAccessCodeRegex = () => eightDigitsAccessCodeValidation;
const accessCodeValidation = (value) => {
    return [
        {
            validation: (0, LodashExtensions_1._isBlank)(value),
            errorMessage: (t) => t('survey:errors:accessCodeRequired')
        },
        {
            validation: !getAccessCodeRegex().test(String(value)),
            errorMessage: (t) => t('survey:errors:accessCodeInvalid')
        }
    ];
};
exports.accessCodeValidation = accessCodeValidation;
//# sourceMappingURL=validations.js.map